import { useEffect, useRef, useState } from "react";
import { LOCALE } from "../../lib/i18n";

/* One IntersectionObserver per option set, shared by every Reveal on the page. Entries that cross in the same
   frame arrive in one callback, so React batches their state changes into a single render instead of one
   render per element – noticeably fewer long frames while scrolling through a floor full of reveals. */
const observers = new Map();
const observe = (el, opts, cb) => {
  const key = `${opts.rootMargin}|${opts.threshold}`;
  let entry = observers.get(key);
  if (!entry) {
    const targets = new Map();
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const fn = targets.get(e.target);
        if (fn) { targets.delete(e.target); io.unobserve(e.target); fn(); }
      }
    }, opts);
    entry = { io, targets };
    observers.set(key, entry);
  }
  entry.targets.set(el, cb);
  entry.io.observe(el);
  return () => { entry.targets.delete(el); entry.io.unobserve(el); };
};

const useInView = (opts = {}) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setOn(true); return; }
    return observe(el, { rootMargin: opts.rootMargin || "0px 0px -8% 0px", threshold: opts.threshold ?? 0.08 }, () => setOn(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, on];
};

/**
 * Enters with a short lift + fade. `soft` is opacity-only – use it on anything that wraps large
 * photography, so the browser never has to promote a viewport-sized bitmap to a moving layer.
 */
export const Reveal = ({ as: Tag = "div", className = "", delay = 0, soft = false, children, style, ...rest }) => {
  const [ref, on] = useInView();
  return (
    <Tag ref={ref} className={`${soft ? "reveal-soft" : "reveal"} ${on ? "is-in" : ""} ${className}`} style={{ "--d": `${delay}ms`, ...style }} {...rest}>
      {children}
    </Tag>
  );
};

/**
 * A curtain (pseudo-element, transform-only, fully composited) lifts off the image once it is in view.
 * The image underneath is never clipped or scaled, so revealing costs nothing but a layer move.
 * `curtain` picks the curtain colour: "surface" (default, on light floors) | "navy" (on dark floors).
 */
export const ClipReveal = ({ as: Tag = "div", className = "", delay = 0, curtain = "surface", children, ...rest }) => {
  const [ref, on] = useInView({ threshold: 0.12 });
  return (
    <Tag ref={ref} className={`clip-reveal overflow-hidden ${on ? "is-in" : ""} ${className}`} data-curtain={curtain} style={{ "--d": `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
};

export const FloorLabel = ({ n, children, light, className = "" }) => (
  <p className={`t-label ${light ? "text-white/70" : ""} ${className}`} style={light ? undefined : { color: "#3E7CB1" }}>
    {n && <><span className="tabular">{n}</span> — </>}{children}
  </p>
);

/** Start fetching this far ahead of the viewport. Native lazy margins are small in Safari, so we drive it ourselves. */
const WARM_MARGIN = "150% 0px 150% 0px";

/**
 * Responsive image with two smoothing layers on top of `loading="lazy"`:
 *  – warm-up: an IntersectionObserver flips lazy → eager ~1.5 screens before the image is needed,
 *    so it is fetched and decoded before it scrolls into view (no pop-in on fast scrolls);
 *  – fade: the bitmap is held at opacity 0 until decoded and then faded in (opacity only, ~350 ms),
 *    so a late image never snaps onto the page. Pass `fade={false}` where the caller animates opacity itself.
 */
export const Img = ({ src, alt = "", sizes = "100vw", className = "", eager = false, fade = true, widths = [480, 800, 1200], onLoad, onError, ...rest }) => {
  const ref = useRef(null);
  const [warm, setWarm] = useState(eager);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (eager || warm) return;
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setWarm(true); return; }
    return observe(el, { rootMargin: WARM_MARGIN, threshold: 0 }, () => setWarm(true));
  }, [eager, warm]);

  // Cached images can be complete before React attaches onLoad.
  useEffect(() => {
    const el = ref.current;
    if (el && el.complete && el.naturalWidth > 0) setLoaded(true);
  }, [src]);

  if (!src) return null;
  const local = src.startsWith("/images/") && /\.(jpg|jpeg|png|webp)$/.test(src);
  const base = local ? src.replace(/\.(jpg|jpeg|png|webp)$/, "") : null;
  const fallback = widths.includes(800) ? 800 : widths[widths.length - 1];
  const cls = `${fade ? "img-fade" : ""} ${fade && loaded ? "is-loaded" : ""} ${className}`;
  const handlers = {
    onLoad: (e) => { setLoaded(true); onLoad?.(e); },
    onError: (e) => { setLoaded(true); onError?.(e); },
  };

  return (
    <img
      ref={ref}
      src={local ? `${base}-${fallback}.webp` : src}
      srcSet={local ? widths.map((w) => `${base}-${w}.webp ${w}w`).join(", ") : undefined}
      sizes={local ? sizes : undefined}
      alt={alt}
      loading={warm ? "eager" : "lazy"}
      decoding="async"
      className={cls}
      data-loaded={fade ? (loaded ? "true" : "false") : undefined}
      {...handlers}
      {...rest}
    />
  );
};

/**
 * Crossfade between images by key – only the current and previous image are mounted,
 * so a stack of N candidates never downloads more than it shows.
 */
export const Crossfade = ({ src, alt = "", sizes = "100vw", className = "", imgClassName = "", duration = 700, eager = false, ...rest }) => {
  const [layers, setLayers] = useState(() => (src ? [{ src, alt, id: 0 }] : []));
  useEffect(() => {
    setLayers((prev) => {
      const top = prev[prev.length - 1];
      if (top && top.src === src) return prev;
      const next = [...prev.slice(-1), { src, alt, id: (top?.id ?? 0) + 1 }];
      return next;
    });
  }, [src, alt]);
  useEffect(() => {
    if (layers.length < 2) return;
    const t = setTimeout(() => setLayers((prev) => prev.slice(-1)), duration + 50);
    return () => clearTimeout(t);
  }, [layers, duration]);
  return (
    <div className={`overflow-hidden ${/\babsolute\b|\bfixed\b/.test(className) ? "" : "relative"} ${className}`} {...rest}>
      {layers.map((l, i) => (
        <Img
          key={l.id}
          src={l.src}
          alt={l.alt}
          sizes={sizes}
          fade={false}
          eager={eager && i === 0 && layers.length === 1}
          data-active={i === layers.length - 1 ? "true" : "false"}
          className={`absolute inset-0 h-full w-full object-cover ${i === layers.length - 1 ? "xfade-in" : ""} ${imgClassName}`}
          style={{ animationDuration: `${duration}ms` }}
        />
      ))}
    </div>
  );
};

export const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Counts up to `value` once visible. Formats with nb-NO.
 * Rushes through the early values and spends most of the duration settling on the last digits,
 * so it reads as an instrument coming to rest rather than a slot machine.
 * `active` (optional) hands the trigger to a parent conductor so several numbers share one timeline; `delay` offsets the start.
 */
export const CountUp = ({ value, decimals = 0, prefix = "", suffix = "", duration = 1400, delay = 0, active, className = "", ...rest }) => {
  const [ref, seen] = useInView({ threshold: 0.3 });
  const on = active ?? seen;
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!on) return;
    if (reduced()) { setV(value); return; }
    let raf, t0;
    const tick = (t) => {
      if (t0 == null) t0 = t;
      const p = Math.min(1, Math.max(0, (t - t0 - delay) / duration));
      setV(value * (1 - Math.pow(1 - p, 4)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [on, value, duration, delay]);
  const fmt = v.toLocaleString(LOCALE, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return <span ref={ref} className={`tabular ${className}`} aria-label={`${prefix}${value.toLocaleString(LOCALE)}${suffix}`} {...rest}>{prefix}{fmt}{suffix}</span>;
};

export { useInView };
